import { createSelector } from "@reduxjs/toolkit";
import { MarkerData } from "./types";
import { sortRecentMarker } from "./utils";

export const EMOJI_MARKER_LABEL = "Emoji Note Marker";

function getBase(state) {
  return state.markers;
}

const labelSort = (a, b) => a.label.localeCompare(b.label);

function isEmojiMarker(m) {
  return m.label === EMOJI_MARKER_LABEL;
}

function getColorNameId(marker) {
  switch (marker.vstColor) {
    case "#ff7979":
      return "red";
    case "#80e1fd":
      return "blue";
    case "#47e6b4":
      return "teal";
    case "#f4ed75":
      return "yellow";
    case "#ffa0ea":
      return "pink";
    case "#aca6ff":
      return "purple";
    case "#e5be8b":
      return "brown";
    case "#a1ec80":
      return "green";
    case "#ffc766":
      return "orange";
    case "#8ac6ff":
      return "darkblue";
    default:
      return "yellow";
  }
}

export const getMarkers = createSelector<any, any, any, MarkerData[]>(
  (state) => getBase(state).markerIds,
  (state) => getBase(state).markerData,
  (markerIds, markerData) =>
    markerIds
      .map((marker) => markerData[marker])
      .sort(sortRecentMarker)
      .filter((m) => !isEmojiMarker(m))
);

export const getLabelSortedMarkers = createSelector<any, any, MarkerData[]>(
  getMarkers,
  (markers) => [...markers].sort(labelSort)
);

export const getHighlightsSubscribedMarkers = (state) =>
  getBase(state).highlightsSubscribedMarkers;
export const getAllMarkers = createSelector<any, any, any, MarkerData[]>(
  getHighlightsSubscribedMarkers,
  getMarkers,
  (subscribedMarkers, mostRecentMarkers) => {
    const { used, unused } = mostRecentMarkers.reduce(
      ({ used, unused }, marker) => {
        if (marker.lastUsedAt) {
          used.push(marker);
        } else {
          unused.push(marker);
        }
        return { used, unused };
      },
      { used: [], unused: [] }
    );
    return used.concat(subscribedMarkers).concat(unused);
  }
);

export const getColorOptions = (state) =>
  getBase(state).colorOptions.map((c) => ({
    ...c,
    labelId: getColorNameId(c),
  }));

export const getColorOptionsLoadStatus = (state) =>
  getBase(state).colorOptionsLoadStatus;

export const getMarkerLoadStatus = (state) => getBase(state).markerLoadStatus;

export const getMarker = (state, markerId) =>
  getBase(state).markerData[markerId];

export const getSharedMarkerCount = createSelector(
  (state) => getBase(state).markerIds,
  (state) => getBase(state).markerData,
  (markerIds, markerData) =>
    markerIds
      .map((marker) => markerData[marker])
      .filter((marker) => marker.shared).length
);

export const getActiveMarkerId = (state) => {
  let activeMarkerId = getBase(state).activeMarkerId;
  if (!activeMarkerId) {
    activeMarkerId = getBase(state).markerIds[0] || "";
  }
  return activeMarkerId;
};

export const getEmojiMarkers = createSelector(
  (state) => getBase(state).markerIds,
  (state) => getBase(state).markerData,
  (markerIds, markerData) =>
    markerIds
      .map((marker) => markerData[marker])
      .filter((m) => isEmojiMarker(m))
);
